import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getLocationsData } from '../../Public/locations-data';
import Image from '../../Utils/Image/image-component';
import NavLink from '../../Utils/NavLink/navlink-component';

function LocationsSection({
    limit = null,
    title = null,
    showMore = false,
}) {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const scrollRef = useRef(null);

    const systemData = useSelector((s) => s.systemData || {});
    const { dir, language } = systemData;

    useEffect(() => {
        const initialize = async () => {
            setLoading(true);
            try {
                const data = await getLocationsData();
                setLocations(data || []);
            } catch (err) {
                console.error("Failed to fetch locations:", err);
            } finally {
                setLoading(false);
            }
        };
        initialize();
    }, []);

    const displayLocations = useMemo(() => {
        return limit ? locations.slice(0, limit) : locations;
    }, [locations, limit]);

    if (!loading && displayLocations.length === 0) return null;

    return (
        <section className="w-full bg-white dark:bg-neutral-950 py-20 lg:py-32 font-assistant overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 mb-12 flex justify-between items-end">
                <div className="flex flex-col">
                    <h2 className="text-4xl md:text-6xl font-black text-neutral-900 dark:text-white er uppercase">
                        {title || (language === 'he' ? 'אזורי שירות' : 'Service Areas')}
                    </h2>
                </div>
                
                {showMore && (
                    <NavLink 
                        href="/locations" 
                        className="text-xs font-black uppercase tracking-widest text-primary-500 mb-2"
                    >
                        {language === 'he' ? 'צפה בכולם' : 'View All'}
                    </NavLink>
                )}
            </div>

            {/* --- THE DISCOVERY SLIDER --- */}
            <div 
                ref={scrollRef}
                className="flex gap-6 overflow-x-auto scrollbar-hide px-6 lg:px-[calc((100vw-80rem)/2)] cursor-grab active:cursor-grabbing pb-10"
            >
                {loading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="shrink-0 w-72 h-96 bg-gray-100 animate-pulse rounded-[2rem]" />
                    ))
                ) : (
                    displayLocations.map((loc) => (
                        <div 
                            key={loc.id}
                            onClick={() => navigate(`/locations/${loc.slug}`)}
                            className="group relative shrink-0 w-72 lg:w-96 aspect-[3/4] rounded-[2.5rem] overflow-hidden cursor-pointer bg-neutral-100 dark:bg-neutral-900 border border-neutral-100 dark:border-white/5 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2"
                        >
                            {/* Image Background */}
                            <Image
                                src={loc.image}
                                alt={loc.name}
                                wrapperClassName="absolute inset-0 w-full h-full opacity-80 group-hover:opacity-100 transition-opacity duration-500"
                                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                            />
                            
                            {/* Technical Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-primary-900 via-transparent to-transparent opacity-60" />

                            {/* Content Block */}
                            <div className="absolute inset-0 p-8 flex flex-col justify-end">
                                <div className="space-y-1 transform transition-transform duration-500 group-hover:-translate-y-2">
                                    <h3 className="text-3xl font-black text-white uppercase er leading-none">
                                        {loc.name}
                                    </h3>
                                    
                                    {/* Using your NavLink logic for the CTA inside the card */}
                                    <div className="pt-4 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-4 group-hover:translate-y-0">
                                        <span className="text-[10px] font-black text-white uppercase tracking-[0.3em] border-b border-white pb-1">
                                            {language === 'he' ? 'פתח מיקום' : 'Explore Zone'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </section>
    );
}

export default LocationsSection;